<script setup>
/**
 * @todo When the legacy theme is removed: delete the legacyProps prop, the useTheme import,
 * and collapse effectiveProps to { ...base, ...attrs }.
 */

import { computed, useAttrs } from 'vue';

import { useTheme } from '../composables/useTheme';

/**
 * M3 card container with elevated, filled, and outlined variants and interaction states.
 *
 * @status internal
 * @group cards-dialogs
 * @see https://quasar.dev/vue-components/card Quasar q-card API
 */
defineOptions({ name: 'HrCard' });

const props = defineProps({
  /**
   * Visual variant following M3 card hierarchy.
   * Determines surface color and baseline elevation.
   * @values elevated, filled, outlined
   */
  variant: {
    type: String,
    default: 'elevated',
    validator: (v) => ['elevated', 'filled', 'outlined'].includes(v),
  },
  /**
   * Corner radius of the card.
   * @values none, xs, sm, md, lg, xl
   */
  shape: {
    type: String,
    default: 'xl',
    validator: (v) => ['none', 'xs', 'sm', 'md', 'lg', 'xl'].includes(v),
  },
  /**
   * Adds hover/active interaction states and cursor-pointer. Use when the whole card is a clickable action.
   */
  clickable: {
    type: Boolean,
    default: false,
  },
  /**
   * Like clickable, but also supports the selected state. Use for card grids where items can be picked.
   */
  selectable: {
    type: Boolean,
    default: false,
  },
  /**
   * Lays out card children in a row. Useful for media cards with a leading image.
   */
  horizontal: {
    type: Boolean,
    default: false,
  },
  /**
   * Whether the card is disabled. Removes pointer events and reduces opacity.
   */
  disabled: {
    type: Boolean,
    default: false,
  },
  /**
   * Whether the card is in a selected state. Only meaningful when selectable is true.
   */
  selected: {
    type: Boolean,
    default: false,
  },
  /**
   * Legacy props are any additional Quasar props that can be passed to the card for backwards
   * compatibility with the legacy theme. These props will be ignored when a design system theme
   * is active.
   * @deprecated This prop is only for backwards compatibility with the legacy theme and should be
   * removed once the legacy theme is removed.
   */
  legacyProps: {
    type: Object,
    default: () => ({}),
  },
});

const attrs = useAttrs();
const { hasTheme } = useTheme();

const classes = computed(() => [
  'hr-card',
  `hr-card--${props.variant}`,
  `hr-card--shape-${props.shape}`,
  (props.clickable || props.selectable) && 'hr-card--interactive',
  props.selectable && 'hr-card--selectable',
  props.horizontal && 'hr-card--horizontal',
  props.disabled && 'hr-card--disabled',
  props.selected && 'hr-card--selected',
]);

const effectiveProps = computed(() => {
  if (hasTheme.value) {
    // No flat/square — those props apply !important on Quasar's side, blocking our CSS variables.
    // Our scoped CSS overrides Quasar's defaults via attribute-selector specificity instead.
    return { ...attrs };
  }
  return { ...attrs, ...props.legacyProps };
});
</script>
<template>
  <q-card
    v-ripple="hasTheme && (clickable || selectable) && !disabled"
    v-bind="effectiveProps"
    :class="hasTheme ? classes : undefined"
    :tabindex="(clickable || selectable) && !disabled ? 0 : undefined"
    :role="(clickable || selectable) && !disabled ? 'button' : undefined"
    :aria-disabled="disabled ? true : undefined"
    @keydown.enter.self.prevent="$event.currentTarget?.click?.()"
    @keydown.space.self.prevent="$event.currentTarget?.click?.()"
  >
    <!-- @slot default Card content — use HrCardSection, HrCardActions, and media elements. -->
    <slot />
  </q-card>
</template>
<style lang="scss" scoped>

/* Only applies to light/dark themes */
.hr-card {
  position: relative;
  overflow: hidden;
  transition: box-shadow 150ms linear;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: var(--app-on-surface);
    opacity: 0;
    border-radius: inherit;
    pointer-events: none;
    transition: opacity 150ms linear;
  }
}

.hr-card--elevated {
  // Surface tint mixed into background per M3 tonal surface system (L1 = 5%, L2 = 8%)
  --hr-card-surface: color-mix(in srgb, var(--app-surface-tint) 5%, var(--app-surface-container-low));
  background: var(--hr-card-surface);
  color: var(--app-on-surface);
  box-shadow: var(--app-elevation-1);
  transition: box-shadow 150ms linear, background 150ms linear;
}

.hr-card--filled {
  --hr-card-surface: var(--app-surface-container-highest);
  background: var(--hr-card-surface);
  color: var(--app-on-surface);
  box-shadow: var(--app-elevation-0);
}

.hr-card--outlined {
  --hr-card-surface: var(--app-surface);
  background: var(--hr-card-surface);
  color: var(--app-on-surface);
  box-shadow: var(--app-elevation-0);
  border: 1px solid var(--app-outline-variant);
}

.hr-card--shape-none { border-radius: var(--app-shape-corner-none); }
.hr-card--shape-xs   { border-radius: var(--app-shape-corner-extra-small); }
.hr-card--shape-sm   { border-radius: var(--app-shape-corner-small); }
.hr-card--shape-md   { border-radius: var(--app-shape-corner-medium); }
.hr-card--shape-lg   { border-radius: var(--app-shape-corner-large); }
.hr-card--shape-xl   { border-radius: var(--app-shape-corner-extra-large); }

.hr-card--interactive:not(.hr-card--disabled) {
  cursor: pointer;

  &:hover::before {
    opacity: 0.08;
  }
}

.hr-card--elevated.hr-card--interactive:not(.hr-card--disabled):not(.hr-card--selected):hover {
  background: color-mix(in srgb, var(--app-surface-tint) 8%, var(--app-surface-container-low));
  box-shadow: var(--app-elevation-2);
}

.hr-card--filled.hr-card--interactive:not(.hr-card--disabled):hover,
.hr-card--outlined.hr-card--interactive:not(.hr-card--disabled):hover {
  box-shadow: var(--app-elevation-1);
}

.hr-card.hr-card--selected {
  background: var(--app-secondary-container);
  color: var(--app-on-secondary-container);

  &::before {
    background: var(--app-on-secondary-container);
  }
}

.hr-card--horizontal {
  display: flex;
  flex-direction: row;
  align-items: stretch;
}

.hr-card--disabled {
  pointer-events: none;
  opacity: 0.38;
}
</style>
